import {
  AUTONOMY,
  BIODIVERSITY,
  CLIMATE,
  ECONOMIC_THEME,
  ECONOMIC_VIABILITY,
  ENVIRONMENTAL_THEME,
  FOOD_SAFETY,
  NATURAL_RESOURCES,
  PUBLIC_HEALTH,
  RESILIENCE,
  SOCIAL_THEME,
  SOIL_FERTILITY,
  TASTE,
  TERRITORY_CONNECTION,
  WORK_CONDITIONS,
} from "./petalesAndThemes";

export const QuizData = [
  {
    id: 0,
    question: `Où situeriez-vous votre ferme dans sa capacité à générer une rémunération décente, maintenir un niveau d’investissements nécessaire pour pérenniser son activité ?
    `,
    details: {
      header: null,
      guideline_preambule: `<p>La viabilité d’une ferme signifie la capacité à générer un revenu décent pour l’agriculteur (dont le montant est très spécifique aux attentes de chacun) en complément d’une capacité à investir pour maintenir des conditions d’exercice confortables, ou épargner pour améliorer la résilience de la ferme.</p>
        <p>A titre indicatif et même si les montants de revenus espérés sont très personnels, on suggère de prendre le SMIC+20% comme plancher pour «noter» la ferme au-delà du niveau 3.</p>
        <p>Les leviers à actionner pour atteindre cette viabilité sont multiples et font référence à la fois à la maitrise des charges et à l’optimisation de la valeur ajoutée côté recettes.
        </p>`,

      guidelines: [
        `<b>1. Rationalisation des charges et investissements.</b> <br>Rationnaliser les charges opérationnelles (production économe en intrants, etc), juste équilibre à trouver dans le niveau d’investissement (favorable à l’efficacité de la production mais raisonnable en poids d’endettement), favoriser l’autoproduction (de plants, d’alimentation, etc)</br><br></br>`,
        `<b>2. Optimisation de la valeur ajoutée…</b><br>Ramener la valeur ajoutée sur la ferme (limitation des intermédiaires, transformation directe),  développer les produits labellisés et la diversification de la production, améliorer l’efficacité sur la ferme (optimisation de l’organisation du travail)</br>
        <br></br>`,
      ],
    },
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur les clefs de viabilité et ne suis pas en mesure de suivre la comptabilité de ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Mon EBE me permet de me verser une très faible rémunération après remboursement des annuités d'emprunt.`,
        level: 1,
      },
      {
        answer: `Mon EBE me permet de me dégager une rémunération encore insatisfaisante après remboursement des annuités d'emprunt. La ferme reste financièrement fragile en cas d’aléas. `,
        level: 2,
      },
      {
        answer: `Je peux me dégager une rémunération dont je suis satisfait depuis plusieurs années. Je maîtrise les clefs de viabilité et ma comptabilité.
        `,
        level: 3,
      },
      {
        answer: `Je peux me dégager une rémunération dont je suis satisfait depuis plusieurs années. Mon EBE, en plus de me permettre de rembourser des annuités et de me dégager ce revenu, me permet d’investir dans ma ferme.
        `,
        level: 4,
      },
    ],
    theme: ECONOMIC_THEME,
    petale: ECONOMIC_VIABILITY.petale,
  },
  {
    id: 1,
    question: `Où situeriez-vous votre ferme dans sa capacité à limiter la pénibilité physique et mentale, permettre de maîtriser le temps de travail et d’introduire des jours de repos ?
    `,
    details: {
      header: null,
      guideline_preambule: `<p>L’amélioration des conditions de travail vise un système où l’agriculteur peut équilibrer
      son activité professionnelle pour l’adapter à ses aspirations personnelles et ses capacités,
      en limitant également la pression sur la santé. Elle s’envisage pour l’agriculteur et l’ensemble de son équipe.
      </p>
      <p>Les leviers à actionner pour atteindre cette viabilité sont multiples :</p>`,
      guidelines: [
        `- Optimiser l’organisation de la ferme et du travail
      `,
        `- Avoir recours à des installations, équipements, outils plus ergonomiques et limiter les pratiques agricoles avec des opérations pénible
      `,
        `- Mutualiser, avoir recours à des prestataires ( à l’emploi d’un salarié/stagiaire/saisonnier ou d’un service de remplacement)
      `,
        `- Intégrer des pratiques managériales bienveillantes 
      `,
        `- Participer à des groupes de progrès pour avancer en collectif et sortir de la solitude
      `,
        `- Limiter la pression financière de remboursements trop importants à honorer
      `,
        `- Etc…<br></br>`,
      ],
    },
    options: [
      {
        answer: `Mes conditions d’exercices ne sont pas acceptables et donc pas durables`,
        level: 0,
      },
      {
        answer: `Mes conditions de travail ne sont acceptables qu’à très court terme.
        `,
        level: 1,
      },
      {
        answer: `Je suis satisfait(e) de mes conditions de travail à court terme mais des aménagements devront avoir lieu pour les maintenir sur le long terme.
        `,
        level: 2,
      },
      {
        answer: `Je suis satisfait(e) de mes conditions de travail même si je peux identifier encore quelques marges de progrès.
        `,
        level: 3,
      },
      {
        answer: `Je suis durablement satisfait(e) de mon organisation, de mon temps de travail, du niveau de pénibilité physique, de la stimulation intellectuelle liée à mon activité et du sens que j’y trouve.`,
        level: 4,
      },
    ],
    theme: ECONOMIC_THEME,
    petale: WORK_CONDITIONS.petale,
  },
  {
    id: 2,
    question: `Où situeriez-vous votre ferme sur sa capacité de résilience ?
    `,
    details: {
      header: null,
      guideline_preambule: `<p>La résilience se pense au niveau du territoire et englobe les actions qui dépassent uniquement le cadre de la ferme. Cependant la résilience de la ferme se travaille tant face aux aléas naturels, aux risques sanitaires qu’aux crises économiques externes (à l’échelle nationale ou internationale).
      </p>`,
      guidelines: [
        `<b>Résister aux aléas naturels (érosion, sécheresse, inondations ……):<b>`,
        `- Optimiser les qualités hydriques des sols, la gestion de l’eau les infrastructures écologiques permettant l’ombrage
        `,
        `- Limitation des risques érosifs via la gestion des sols et la mise en place d’infrastructures écologiques en rupture de pente
        `,
        `- Limiter la perte de biodiversité et reconstruire des écosystèmes grâce aux plantations et protections spécialisées
        `,
        `- Limiter les traitements antibiotiques pour éviter les résistances bactériennes en élevage
         `,
        `<br></br><b>Résister aux aléas économiques (variation des rendements et cours mondiaux, du prix du pétrole et des matières premières ……), sanitaires (pandémies) et politiques (boycott, fermeture de frontières, hausse des taxes,...)
        </b>`,
        `- Diversifier les productions et les modes de commercialisation
        `,
        `- Limiter sa dépendance aux intrants ou débouchés soumis aux aléas liés à la globalisation
        `,
        `- Impliquer les consommateurs dans le modèle de gouvernance de la ferme
        <br></br>`,
      ],
    },
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune.`,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.
        `,
        level: 2,
      },
      {
        answer: `Je mets en pratique les dimensions me permettant d’être théoriquement résilient(e) face aux aléas environnementaux et économiques. `,
        level: 3,
      },
      {
        answer: `Je mets en pratique les dimensions me permettant d’être théoriquement résilient(e) face aux aléas environnementaux et économiques. <b>J’ai pu le constater en y étant déjà confronté(e).</b>`,
        level: 4,
      },
    ],
    theme: ECONOMIC_THEME,
    petale: RESILIENCE.petale,
  },
  {
    id: 3,
    question: `Où situeriez-vous votre ferme sur son autonomie vis à vis des ressources extérieures pour assurer la production ?

    `,
    details: {
      header: null,
      guideline_preambule: `<p>La notion d’autonomie englobe les questions de dépendance en intrants (énergies fossiles ou renouvelables, fertilité des sols, nutrition des animaux), l’autonomie au niveau de la gestion du matériel, la capacité pour l’agriculteur à prendre ses propres décisions ( commercialisation, foncier) et l’autonomie dans le souhait d’investir selon les aspirations et capacités de l’agriculteur. L’autonomie ne signifie pas autarcie et doit donc parfois s’envisager à l’échelle de la ferme mais aussi dans un territoire et un écosystème humain proche.
      </p>`,
      guidelines: [
        `<b>Limiter la dépendance en intrants (énergies fossiles ou renouvelables, fertilité des sols, nutrition des animaux), 
      </b>`,
        `-  Assurer l’autoproduction des semences et de la matière organique,
      `,
        `-  Assurer l’autonomie alimentaire pour l’élevage avec des fourrages et aliments issus de la ferme ou mettre en place des boucles locales et courtes entre les productions végétales et animales
      `,
        `-  Assurer l’auto insémination et la saillie naturelle
      `,
        `-  Développer les circuits de commercialisation en vente directe
      <br></br>`,
        `<b>Autonomie décisionnelle et financière</b>
      `,
        `- Maitriser sa comptabilité
        `,
        `- Maîtriser le financement de son projet
        `,
        `- Choisir un matériel facilement réparable et recyclable (montée en compétence de l’autoréparation et auto construction)
      <br></br>`,
      ],
    },
    options: [
      {
        answer: `Mon niveau d’autonomie est très faible`,
        level: 0,
      },
      {
        answer: `Mon niveau d’autonomie est assez faible`,
        level: 1,
      },
      {
        answer: `Mon niveau d’autonomie reste moyen et implique, en conséquence, une résilience encore limitée`,
        level: 2,
      },
      {
        answer: `Mon niveau d’autonomie est très poussé.
        `,
        level: 3,
      },
      {
        answer: `Mon niveau d’autonomie est extrêmement poussé et stable depuis plusieurs années.`,
        level: 4,
      },
    ],
    theme: ECONOMIC_THEME,
    petale: AUTONOMY.petale,
  },
  {
    id: 4,
    question: `Où situeriez-vous votre ferme sur la préservation ou la régénération de la biodiversité ? 
    `,
    details: {
      header: null,
      guideline_preambule: `<p>Si l’on souhaite aborder l’enjeu biodiversité de manière complète sur une ferme, il faut prendre en compte les dimensions suivantes :
      </p>`,
      guidelines: [
        `<b>Favoriser la biodiversité dans les infrastructures écologiques (haies, bandes enherbées fleuries, mares, fossés…) :</b>`,
        `1. Implanter ou préservez (s'ils existaient déjà sur la ferme) ces infrastructures écologiques en quantité suffisante et en diversité suffisante (permettant donc d'accueillir une grande diversité d'espèces animales et végétales)`,
        `2. Assurez le fait que ces infrastructures écologiques soient connectées entre elles à l'échelle de la ferme (corridors)`,
        `3. S'assurer que leur composition (essences de la flore) et leur gestion (taille, fauche, etc..) est favorable aux cycles biologiques (notamment assurer une floraison continue sur la ferme pour l'alimentation des insectes, respecter les habitats lors des periodes de nidification, etc..)`,
        `<b> Préserver la biodiversité dans le champ :</b>`,
        `4. Mettre en place les pratiques favorisant la biodiversité du sol adaptés à mon contexte (réduction du travail du sol, de la fertilisation minérale et des produits phytosanitaires)`,
        `5. Mettre en place les pratiques favorisant la biodiversité dans le compartiment aerien du champ (continuité du couvert végétal et de la floraison, bio-contrôle et lutte biologique, diversité cultivée et temporelle, rotations longues, médecines alternatives (homéopathie, aromathérapie, etc.)`,
        `6. Mettre en place les pratiques favorisant l’agri biodiversité, c’est à dire la diversité cultivée ou élevée (diversification des espèces et choix des plus adaptées au contexte pédoclimatique local, promotion des associations de cultures, mise en place des rotations longues ou encore reproduction des semences pour renforcer l’adaptation locale, préservation des races et variétés locales et anciennes)<br></br>`,
      ],
    },
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune.`,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.`,
        level: 2,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        `,
        level: 3,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        Je mesure mes résultats : <b>ma ferme démontre un impact positif sur la biodiversité.</b>`,
        level: 4,
      },
    ],

    theme: ENVIRONMENTAL_THEME,
    petale: BIODIVERSITY.petale,
  },
  {
    id: 5,
    question: `Où situeriez-vous votre ferme sur la préservation ou la régénération de la fertilité endogène à long terme de vos sols ?`,
    details: {
      header: null,
      guideline_preambule: `<p>Si l’on souhaite améliorer année après année la fertilité naturelle de ses sols, il faut prendre en compte les dimensions suivantes et les combiner en les adaptant à son contexte :
      </p>`,
      guidelines: [
        `<p><b>1. Maximiser la couverture du sol à l’échelle de la rotation</b>, en priorité par des plantes vivantes (en mettant en place des intercultures, des couverts permanents) ou par des couverts importés (paillage, bâche, toile…). Cap indicatif : plus de 80% de taux de couverture du sol par les plantes vivantes à l’échelle de la rotation.
        </p>`,
        `<p><b>2. Diversifier les cultures et couverts végétaux</b> à travers les rotations, les associations de cultures ou encore les mélanges variétaux. Cap indicatif : plus de 8 espèces cultivées à l’échelle de la rotation.
        </p>`,
        `<p><b>3. Limiter le travail du sol,</b> en fonction de son contexte notamment en limitant le travail profond par exemple en mettant en place des techniques culturales simplifiées avec un travail du sol superficiel, peu profond et non systématique, voire une absence de travail du sol. 
        </p>`,
        `<p><b>4. Mettre en place les pratiques stimulant les cycles biologiques et la vie du sol,</b> notamment en favorisant les intrants organiques frais (fumier, compost…) ou ligneux (paille, broyat…), en raisonnant et limitant la fertilisation minérale en limitant ou supprimant l’utilisation de produits phytosanitaires préjudiciables à la vie du sol (fongicides, insecticides..).
      <br></br></p>`,
      ],
    },
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune.`,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.
        `,
        level: 2,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        `,
        level: 3,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        Je mesure mes résultats : <b>ma ferme démontre un impact positif sur la fertilité naturelle de mes sols.</b>`,

        level: 4,
      },
    ],
    theme: ENVIRONMENTAL_THEME,
    petale: SOIL_FERTILITY.petale,
  },
  {
    id: 6,
    question: `Où situeriez-vous votre ferme sur la réduction de ses émissions de gaz à effet de serre (GES) et sa consommation d’énergie ? 
    `,
    details: {
      header: null,
      guideline_preambule: `<p>Si l’on souhaite aborder l’enjeu du climat et de l’énergie de manière complète sur une ferme, il faut prendre en compte les dimensions suivantes :
        </p>`,
      guidelines: [
        `<b>Limiter les émissions de GES et la consommation d’énergie sur l’exploitation :</b>`,
        `- Limiter les émissions liées aux pratiques de fertilisation (fertilisation minérale azotée en particulier)
        `,
        `- Maximiser l’autonomie alimentaire et le pâturage pour les élevages.
        `,
        `- Limiter les émissions liées au déstockage de carbone des sols
        `,
        `- Limiter les émissions liées à la mécanisation et au transport
        `,
        `- Limiter l’énergie grise et du bilan complet de l’outil de production (consommables issus d’énergies fossiles : bâches plastiques
          `,
        `- Mettre en place des dispositifs de production d’énergie renouvelable ou de «recyclage d’énergie» (comme un récupérateur de chaleur dans les bâtiments)
        <br></br>`,
        `<b>Favoriser la séquestration de carbone :</b>
        `,
        `- Mettre en place des pratiques de stockage au niveau des sols : prairies permanentes, couverts végétaux, engrais vert, apports organiques…
        `,
        `- Mettre en place, préserver ou restaurer des Surfaces d’Intérêt Ecologiques (SIE) comme les haies, les bandes enherbées, les mares en quantité et en qualité pour optimiser la capture du carbone.
        `,
        `- Mettre en place des pratiques d’agroforesterie avec des essences productrices de biomasse et une taille qui favorisent le stockage du carbone.
        <br></br>`,
      ],
    },
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune. 
        `,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.`,
        level: 2,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        `,
        level: 3,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme. Je mesure mes résultats : <b>ma ferme émet peu de GES, en stocke davantage et a donc un bilan carbone positif</b>.
        `,
        level: 4,
      },
    ],
    theme: ENVIRONMENTAL_THEME,
    petale: CLIMATE.petale,
  },
  {
    id: 7,
    question: `Où situeriez-vous votre ferme sur son usage des ressources naturelles non renouvelables (matières premières extractives, minéraux) et la préservation de la qualité des biens communs (eau, air, sol) ?
    `,
    details: {
      header: null,
      guideline_preambule: `<p>Si l’on souhaite aborder l’enjeu du climat et de l’énergie de manière complète sur une ferme, il faut prendre en compte les dimensions suivantes :</p>`,
      guidelines: [
        `<b>1. Limiter l’utilisation quantitative de l’eau.</b> Il est possible d’améliorer les propriétés hydriques des sols ou d’adopter des pratiques limitant l’évaporation de l’eau afin de limiter l’irrigation, d’adopter des pratiques d’irrigation efficientes, de choisir des cultures et variétés plus résistantes à la sécheresse, de mettre en place des dispositifs de récupération d’eau …
        <br></br>`,
        `<b>2. Préserver qualitativement la ressource en eau (risque érosif et pollution).</b> Il est possible de réduire les risques érosifs (infrastructures écologiques, couverture des sols…), de limiter les contaminations de l’eau par la réduction de l’usage de produits polluants, d’améliorer les propriétés d’infiltration des sols.
        <br></br>`,
        `<b>3. Réduire les besoins en minéraux et matériaux divers.</b> Réduction des besoins en matière fertilisantes ou traitantes issues de l’extraction minière par le développement d’alternatives organiques. Limitation de la dépendance aux outils numériques (privilégier les approches lowtech), construire ou rénover des bâtiments agricoles à partir de matériaux locaux et bio-sourcés, recyclage des matériaux divers et réparation des engins agricoles.`,
      ],
    },
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune.`,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.
        `,
        level: 2,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        `,
        level: 3,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme. Je mesure mes résultats : <b>la gestion des ressources sur ma ferme est maitrisée.</b>
        `,
        level: 4,
      },
    ],
    theme: ENVIRONMENTAL_THEME,
    petale: NATURAL_RESOURCES.petale,
  },
  {
    id: 8,
    question: `Où situeriez-vous votre ferme sur l’enjeu de la sécurité alimentaire ? 
    `,
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune. `,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.
        `,
        level: 2,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        `,
        level: 3,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme. Je mesure mes résultats : <b>je suis capable de chiffrer la contribution de ma ferme à un régime alimentaire moyen en terme d’apports divers et de calories, et d’objectiver les rendements de ma ferme par unité de surface.</b> 
        `,
        level: 4,
      },
    ],
    details: {
      header: null,
      guideline_preambule:
        "<p>Si l’on souhaite aborder l’enjeu de la sécurité alimentaire de manière complète sur une ferme, il faut prendre en compte les dimensions suivantes :</p>",
      guidelines: [
        `<b>1. Contribuer à son échelle à nourrir la population en quantité suffisante.</b> 
        Cet aspect peut se traduire par l’efficacité de production de la ferme (rendements) et la capacité à Mettre en place une production avec des rendements suffisants par unité de surface
        <br></br>`,
        `<b>2. Contribuer à l’assiette saine et durable en termes de calories alimentaires et de typologie d’aliments.</b> Diversifier les productions et contribuer à la production de calories alimentaires, améliorer la qualité nutritionnelle par le choix des espèces et des techniques, supprimer les contaminants et substances controversées, développer de nouvelles filières de production de protéines végétales pour l’alimentation humaine, notamment des productions végétales fortement caloriques.
        <br></br>`,
        `<b>3. Favoriser l’accès aux produits de qualité pour tous, économiquement et géographiquement.</b>
        <br></br>`,
      ],
    },
    theme: SOCIAL_THEME,
    petale: FOOD_SAFETY.petale,
  },
  {
    id: 9,
    question: `Où situeriez-vous votre ferme sur la préservation de votre santé, celle de vos employés,  de vos consommateurs et de vos voisins?
    `,
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune. 
        `,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.
        `,
        level: 2,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        `,
        level: 3,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme. Je mesure mes résultats :<b> ma ferme démontre qu’elle n’affecte en aucune manière la santé publique.</b>
        `,
        level: 4,
      },
    ],
    details: {
      header: null,
      guideline_preambule:
        "<p>Si l’on souhaite aborder l’enjeu de la santé publique de manière complète sur une ferme, il faut prendre en compte les dimensions suivantes :</p>",
      guidelines: [
        `<b>1. Protéger les humains (agriculteurs, riverains et consommateurs) et leur santé.</b> Limiter les traitements phytosanitaires y compris les traitements autorisés en bio mais potentiellement dangereux à forte dose.
        <br></br>`,
        `<b>2. Améliorer la qualité nutritionnelle des productions.</b> Utiliser des variétés porteuses d’une bonne qualité nutritionnelle, préserver la qualité nutritionnelle, la fraicheur et la maturité des produits à travers des modes de production et des circuits de distribution adaptés, limiter les étapes de transformations et l’usage d’additifs, participer à une filière d’excellence sur la qualité nutritionnelle
        <br></br>`,
        `<i><b>Dans le cas d’un élevage,</b> deux aspects supplémentaires sont à analyser:</i>
        `,
        `<b>3. Limiter la résistance bactérienne dans l’élevage</b> en recourant aux traitements phytothérapeutiques et homéopathiques, en limitant l’usage des antibiotiques ou en prévenant les principaux risques par le biais de pratiques vertueuses (alimentation, hygiène, logement des animaux, conduite du troupeau).
        <br></br>`,
        `<b>4. Améliorer le bien-être animal</b> en allongeant la période de pâturage, en installant des aménagements spécifiques, etc.
        <br></br>`,
      ],
    },
    theme: SOCIAL_THEME,
    petale: PUBLIC_HEALTH.petale,
  },
  {
    id: 10,
    question: `Où situeriez-vous votre ferme sur la préservation des savoir-faire gastronomiques,  des variétés ou espèces anciennes et de votre terroir?
    `,
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune. 
        `,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.
        `,
        level: 2,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        `,
        level: 3,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme. Je mesure mes résultats : <b>la qualité gustative de mes productions est reconnue.</b>
        `,
        level: 4,
      },
    ],
    details: {
      header: null,
      guideline_preambule:
        "<p>Si l’on souhaite aborder l’enjeu de la connexion au territoire de manière complète sur une ferme, il faut prendre en compte les dimensions suivantes :</p>",
      guidelines: [
        `<b>1. La qualité gustative et les typicités des aliments</b> et la <b>mise en place de modes de production ou de transformation d’excellence.</b> Choisir des variétés, espèces et races anciennes et locales. Favoriser la fraicheur et la maturité des produits. Adapter les circuits de distribution (commercialisation local et en circuit court). Partager les pratiques et espèces au sein du réseau agricole local. Respecter les cahiers des charges AOP, IGP. Construire ou rénover les bâtiments agricoles
        <br></br>`,
        `<b>2. Le respect de l’identité paysagère locale.</b> Construire ou rénover les bâtiments agricoles dans le respect de l’identité locale à partir de matériaux locaux et bio-sourcés.
        <br></br>`,
      ],
    },
    theme: SOCIAL_THEME,
    petale: TASTE.petale,
  },
  {
    id: 11,
    question: `Où situeriez-vous votre ferme sur ses liens avec les acteurs de votre territoire et la reconnexion des citoyens à leur alimentation ?
    `,
    options: [
      {
        answer: `Je ne me sens pas suffisamment formé(e) sur la plupart de ces dimensions et je ne les prends pas en compte sur ma ferme.
        `,
        level: 0,
      },
      {
        answer: `Je ne prends en compte que certaines de ces dimensions, sur une partie de ma ferme uniquement et de manière très limitée pour chacune.`,
        level: 1,
      },
      {
        answer: `Je prends en compte la plupart de ces dimensions, mais sur une partie de ma ferme uniquement ou de manière limitée pour certaines.
        `,
        level: 2,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme.
        `,
        level: 3,
      },
      {
        answer: `Je prends en compte l’ensemble de ces dimensions, et chacune de manière approfondie sur l’ensemble de ma ferme. Je mesure mes résultats : <b>ma ferme démontre de vrais impacts sur la connexion avec son territoire.</b>
        `,
        level: 4,
      },
    ],
    details: {
      header: null,
      guideline_preambule:
        "<p>Si l’on souhaite aborder l’enjeu de la connexion au territoire de manière complète sur une ferme, il faut prendre en compte les dimensions suivantes :</p>",
      guidelines: [
        `<b>1. De la reconnexion des citoyens et des consommateurs à leur alimentation.</b> Informer et sensibiliser les clients de la ferme pour leur permettre de devenir consom’acteurs, développer des circuits de proximité et de vente directe, proposer des activités pédagogiques à la ferme via des chantiers participatifs, impliquer les citoyens et acteurs du territoire dans la gouvernance de la ferme
        <br></br>`,
        `<b>2. De la contribution de la ferme dans la vie locale.</b> S’impliquer dans les structures associatives locales en particulier celles promouvant l’agroécologie, accueillir des stagiaires ou recruter des employés locaux
        <br></br>`,
      ],
    },
    theme: SOCIAL_THEME,
    petale: TERRITORY_CONNECTION.petale,
  },
];
